import React, { useState } from 'react';
import emailIcon from '../../icons/waitlist_form/email.svg';
import { Loader2 } from 'lucide-react';
import password from '../../icons/waitlist_form/password.svg';
import { useDispatch, useSelector } from 'react-redux';
import firstNameIcon from '../../icons/waitlist_form/firstname.svg';
import {
  setAuthTokenAction,
  setRefreshTokenAction,
  setUserDataAction,
  setVerificationStatusAction,
} from '../../reduxstore/actions/authActions';
import { useNavigate } from 'react-router-dom';
import GoogleAuthButton from '@/js/components/GoogleAuthButton';
import createApiClient from '@/api/axiosInstance';
import leftHalfImage from '@/js/images/signup-select.webp';

const SignUpPage = ({ setIsSignupComplete }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const [error, setError] = useState('');

  const reduxData = useSelector((state) => state?.authReducer);
  // for back-end validation
  const [processing, setProcessing] = useState(false);

  const [focusedInput, setFocusedInput] = useState('');

  // Helper function to extract first and last name from full name
  const splitFullName = (fullName) => {
    if (!fullName) return { firstName: '', lastName: '' };
    const names = fullName.split(' ');
    const firstName = names[0] || '';
    const lastName = names.slice(1).join(' ') || '';
    return { firstName, lastName };
  };

  const [formData, setFormData] = useState(() => {
    const { firstName, lastName } = splitFullName(reduxData?.userData?.name);

    return {
      first_name: firstName || reduxData?.userData?.first_name || '',
      last_name: lastName || reduxData?.userData?.last_name || '',
      company_email: reduxData?.userData?.email || '',
      password: '', //  initialized password fields as empty for security
      confirm_password: '',
    };
  });

  const [formErrors, setFormErrors] = useState({
    first_name: '',
    last_name: '',
    company_email: '',
    password: '',
    confirm_password: '',
  });

  const validateField = (name, value, formData) => {
    setFormErrors((prevErrors) => {
      const newErrors = { ...prevErrors };

      switch (name) {
        case 'first_name':
          if (value.trim() === '') {
            newErrors.first_name = 'First name is required';
          } else if (/\s/.test(value)) {
            newErrors.first_name = 'Name should not have spaces';
          } else if (/\d/.test(value)) {
            newErrors.first_name = 'Cannot contain numbers';
          } else if (/[^A-Za-z'-]/.test(value)) {
            newErrors.first_name = 'Name contains invalid characters';
          } else {
            newErrors.first_name = '';
          }
          break;

        case 'last_name':
          if (value.trim() === '') {
            newErrors.last_name = 'Last name is required';
          } else if (/\d/.test(value)) {
            newErrors.last_name = 'Cannot contain numbers';
          } else if (/[^A-Za-z\s'-]/.test(value)) {
            newErrors.last_name = 'Invalid characters';
          } else if (/\s{2,}/.test(value)) {
            newErrors.last_name = 'Cannot have consecutive spaces';
          } else {
            newErrors.last_name = '';
          }
          break;

        case 'company_email':
          newErrors.company_email =
            value.trim() === ''
              ? 'Email is required'
              : value.split('@').length !== 2
                ? 'Email not valid'
                : !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                      value,
                    )
                  ? 'Email is not valid'
                  : '';
          break;

        case 'password':
          newErrors.password =
            value.trim() === '' || value.length < 8
              ? 'Password must be at least 8 characters long'
              : '';
          break;
        case 'confirm_password':
          newErrors.confirm_password =
            value.trim() === '' || value.length < 8
              ? 'Confirm your password'
              : value !== formData.password
                ? "Passwords don't match"
                : '';
          break;
        default:
          break;
      }
      return newErrors;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Trim leading and trailing spaces
    const trimmedValue = value.trim();

    setFormData((prevData) => ({
      ...prevData,
      [name]: trimmedValue,
    }));

    // Pass updated formData with the trimmed value to validateField
    validateField(name, trimmedValue, { ...formData, [name]: trimmedValue });
  };

  // TODO: Remove all console logs after testing
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    let valid = true;
    Object.keys(formData).forEach((field) => {
      if (formData[field].trim() === '') {
        validateField(field, formData[field]);
        valid = false;
      }
    });

    if (valid) {
      const full_name = `${formData.first_name} ${formData.last_name}`;
      const signupPayload = {
        name: full_name,
        email: formData.company_email,
        password: formData.password,
      };
      console.log('Attempting to signup with:', signupPayload);

      setProcessing(true);

      try {
        const signupResponse = await apiClient.post(
          '/signup/native',
          JSON.stringify(signupPayload),
          {
            authRequired: false,
          },
        );

        const signupData = signupResponse.data;
        const isOk =
          signupResponse.status >= 200 && signupResponse.status < 300;

        if (!isOk || !signupData.success) {
          setError(signupData.message);
          console.log('Signup failed:', signupData.message);
          throw new Error(signupData.message || 'Signup failed');
        }

        if (signupData.data.x_auth_key && signupData.data.refresh_token) {
          // Dispatch actions - localStorage handling is done in reducer
          await Promise.all([
            dispatch(setAuthTokenAction(signupData.data.x_auth_key)),
            dispatch(setRefreshTokenAction(signupData.data.refresh_token)),
            dispatch(
              setUserDataAction({
                name: formData.first_name + ' ' + formData.last_name,
                email: formData.company_email,
                isVerified: false,
              }),
            ),
            dispatch(setVerificationStatusAction(false)),
          ]);

          localStorage.setItem(
            'authKeyExpiredAt',
            signupData.data.auth_key_expired_at,
          );
          localStorage.setItem(
            'refreshTokenExpiredAt',
            signupData.data.refresh_token_expiration,
          );
          localStorage.setItem('accessToken', signupData.data.x_auth_key);
          localStorage.setItem('refreshToken', signupData.data.refresh_token);
          console.log('Signup successful, redirecting to verification page');

          navigate('/signup/verification', { replace: true });
        } else {
          console.log('Signup failed:', signupData.data.error_message);
          setError(signupData.data.error_message);
        }
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        setError(error_message || 'An error occurred during signup');
        setFormErrors((prev) => ({
          ...prev,
          general: error_message || error.message,
        }));
      } finally {
        setProcessing(false);
      }
    }
  };

  return (
    <div className="splitinhalf flex justify-center min-h-[calc(100vh-70px)] ">
      {/* ---- First Half ---- */}
      <div className="half-1 lg:flex w-1/2 items-center justify-center hidden">
        <div className="flex mr-28 flex-col items-end justify-end transform transition-transform duration-300 lg:scale-90 md:scale-75">
          {/* ----- Left Image ----- */}
          <img
            src={leftHalfImage}
            alt="signup-hero"
            width={640}
            className="transform transition-transform duration-300"
          />
          {/* ----- Box UI ----- */}
          <div
            className="piece-box w-[350px] h-[124px] fixed rounded-3xl text-[15px] p-5 text-gray-600 text-center"
            style={{
              background:
                'linear-gradient(97.27deg, rgba(118, 54, 217, 0.09) 5.17%, rgba(250, 250, 250, 0.6) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.11), rgba(0, 0, 0, 0.12))',
              // Scale down width and height while keeping the box in the same fixed position
              // transform: 'scale(0.9)', // Apply the scale directly to the box
              transformOrigin: 'top left', // Ensures the box scales down from the top right corner
            }}
          >
            Find the perfect manufacturer, get quotes, and manage purchase
            orders effortlessly. Bring your medical device to market faster!
          </div>
        </div>
      </div>

      {/* ---- Second Half ---- */}
      <div className="half-2 w-full xl:w-1/2 flex bg-gray-100 items-center overflow-y-auto xl:justify-center justify-center">
        <div className="form-container mt-3 w-5/6 p-3 flex-col">
          <div className="form-box flex-col text-center">
            <div className="flex justify-center">
              <img
                src="./blackLogo.svg"
                className="w-[67px] "
                alt="MakerSharks-logo"
              />
              <h1 className="items-center flex ml-3 font-roboto text-2xl lg:text-3xl font-semibold">
                MakerSharks
              </h1>
            </div>
            <p className="mt-4 text-base lg:text-lg text-gray-500 text-center">
              Become a part of our network and start connecting
            </p>
            <p className="text-base lg:text-lg mb-5 lg:mb-8 text-gray-500 text-center">
              with others in your industry.
            </p>
          </div>
          {/* Form testitg */}
          <div className="w-full max-w-xl mx-auto">
            <div className="px-8 py-5 h-5/6 relative ">
              <div className="mb-2">
                <GoogleAuthButton signup={true} setError={setError} />
              </div>

              {/* form start */}
              {/* 1. Full Name inputs */}
              <span className="font-roboto font-semibold text-sm mb-2 text-gray-500">
                {' '}
                Full Name
              </span>
              <div className="grid grid-cols-2 gap-3.5">
                <div className="relative ">
                  <input
                    type="text"
                    id="firstName"
                    name="first_name"
                    placeholder=" "
                    onFocus={() => setFocusedInput('first_name')}
                    onBlur={() => setFocusedInput('')}
                    onChange={handleInputChange}
                    value={formData.first_name}
                    className={`peer bg-gray-100 w-full border border-[#575757] border-opacity-30 p-3 ${
                      focusedInput === 'first_name' ? 'pl-4 pt-4 pb-2' : 'pl-10'
                    } pr-3 rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                  />
                  <span
                    className={`absolute inset-y-0 bottom-5 left-0 flex items-center pl-3 transition-opacity duration-300 peer-focus:opacity-0`}
                  >
                    <img
                      src={firstNameIcon}
                      className="h-5 w-5 text-[#575757]"
                      alt="First Name Icon"
                    />
                  </span>
                  {(!formData.first_name || focusedInput === 'first_name') && (
                    <label
                      htmlFor="firstName"
                      className={`absolute text-[12px]  ${
                        focusedInput === 'first_name' ? 'left-1' : 'left-10'
                      }  top-3 text-[#575757] transition-all text-opacity-70 duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                    >
                      First Name
                    </label>
                  )}
                  <p
                    className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                      formErrors.first_name !== '' ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {formErrors.first_name || 'First Name is required'}
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    id="lastName"
                    name="last_name"
                    placeholder=" "
                    onFocus={() => setFocusedInput('last_name')}
                    onBlur={() => setFocusedInput('')}
                    onChange={handleInputChange}
                    value={formData.last_name}
                    className={`peer bg-gray-100 w-full border border-[#575757] border-opacity-30 p-3 ${
                      focusedInput === 'last_name' ? 'pl-4 pt-4 pb-2' : 'pl-4'
                    } rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                  />
                  {(!formData.last_name || focusedInput === 'last_name') && (
                    <label
                      htmlFor="lastName"
                      className={`absolute   ${
                        focusedInput === 'last_name' ? 'left-1' : 'left-3'
                      }  top-3 text-[#575757] text-opacity-70 transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                    >
                      Last Name
                    </label>
                  )}
                  <p
                    className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                      formErrors.last_name !== '' ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {formErrors.last_name || 'last Name is required'}
                  </p>
                </div>
              </div>
              {/* 2. Company name input
              <span className="font-roboto font-semibold text-sm text-gray-500">
                {' '}
                Company Name
              </span>
              <div className="relative ">
                <input
                  type="text"
                  id="companyName"
                  name="company_name"
                  placeholder=" "
                  onFocus={() => setFocusedInput('company_name')}
                  onBlur={() => setFocusedInput('')}
                  onChange={handleInputChange}
                  value={formData.company_name}
                  className={`peer w-full bg-gray-100 border border-[#575757] border-opacity-30 p-3 ${
                    focusedInput ==='company_name' ? 'pl-4 pt-4 pb-2' : 'pl-10'
                  } pr-3 rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                />
                <span
                  className={`absolute inset-y-0 bottom-5 left-0 flex items-center pl-3 transition-opacity duration-300 ${
                    focusedInput ==='company_name'
                      ? 'opacity-0'
                      : 'opacity-100'
                  }`}
                >
                  <img
                    src={companyIcon}
                    className="h-5 w-5 text-[#575757]"
                    alt="Company Name Icon"
                  />
                </span>
                {(!formData.company_name ||
                  focusedInput ==='company_name') && (
                  <label
                    htmlFor="companyName"
                    className={`absolute  ${
                      focusedInput ==='company_name' ? 'left-1' : 'left-10'
                    }  top-3 text-[#575757]  text-opacity-70 transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                  >
                    Enter your company name
                  </label>
                )}
                <p
                  className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                    formErrors.company_name !== '' ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {formErrors.company_name || 'Company Name is required'}
                </p>
              </div> */}
              {/* 3. Select role input */}
              {/* <div className="relative">
                <div className="">
                  <label className="block text-sm font-semibold font-roboto text-gray-500 ">
                    Join us as a
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center justify-start border border-[#575757]  border-opacity-30 rounded-md py-3 px-4 cursor-pointer">
                      <input
                        type="radio"
                        name="role"
                        value="Buyer"
                        checked={formData.role ==='Buyer'} // Ensure only the radio buttons re-render
                        onChange={handleInputChange}
                        className="mr-2 bg-gray-100 cursor-pointer accent-darkPurple w-4 h-4 border-1 focus:outline-none"
                      />
                      <span className="cursor-pointer text-gray-500 font-semibold text-[17px] font-roboto">
                        Buyer
                      </span>
                    </label>
                    <label className="flex items-center justify-start border border-[#575757] rounded-md py-2  border-opacity-30 px-4 cursor-pointer">
                      <input
                        type="radio"
                        name="role"
                        value="Manufacturer"
                        checked={formData.role ==='Manufacturer'}
                        onChange={handleInputChange}
                        className="mr-2 cursor-pointer accent-[#7636D9] w-4 h-4 border-1 rounded-md bg-gray-100 focus:outline-none"
                      />
                      <span className="cursor-pointer text-gray-500 font-semibold text-[17px] font-roboto">
                        Manufacturer
                      </span>
                    </label>
                  </div>
                  <p
                    className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                      formErrors.role !== '' ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {formErrors.role || 'Please select a role'}
                  </p>
                </div>
              </div> */}
              {/* 4. Company email and number */}
              <span className="font-roboto font-semibold text-sm text-gray-500">
                {' '}
                Company Email
              </span>
              <div className="relative">
                <input
                  type="email"
                  id="companyEmail"
                  name="company_email"
                  placeholder=" "
                  onFocus={() => setFocusedInput('company_email')}
                  onBlur={() => setFocusedInput('')}
                  onChange={handleInputChange}
                  value={formData.company_email}
                  className={`peer bg-gray-100 w-full border border-[#575757] border-opacity-30 p-3 ${
                    focusedInput === 'company_email'
                      ? 'pl-4 pt-4 pb-2'
                      : 'pl-10'
                  } pr-3 rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                />
                <span
                  className={`absolute inset-y-0 bottom-5 left-0 flex items-center pl-3 transition-opacity duration-300 ${
                    focusedInput === 'company_email'
                      ? 'opacity-0'
                      : 'opacity-100'
                  }`}
                >
                  <img
                    src={emailIcon}
                    className="h-5 w-5 text-[#575757]"
                    alt="Email Icon"
                  />
                </span>
                {(!formData.company_email ||
                  focusedInput === 'company_email') && (
                  <label
                    htmlFor="companyEmail"
                    className={`absolute text-sm ${
                      focusedInput === 'company_email' ? 'left-1' : 'left-10'
                    }  top-3 text-[#575757] text-opacity-70 transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                  >
                    Enter company email
                  </label>
                )}
                <p
                  className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                    formErrors.company_email !== ''
                      ? 'opacity-100'
                      : 'opacity-0'
                  }`}
                >
                  {formErrors.company_email || 'Company Email is required'}
                </p>
              </div>
              {/* <div className="relative">
                  <input
                    type="text"
                    id="companyNumber"
                    name="company_number"
                    placeholder=" "
                    onFocus={() => setFocusedInput('company_number')}
                    onBlur={() => setFocusedInput('')}
                    onChange={handleInputChange}
                    value={formData.company_number}
                    className={`peer w-full border bg-gray-100 border-[#575757] border-opacity-30 p-3 ${
                      focusedInput ==='company_number'
                        ? 'pl-4 pt-4 pb-2'
                        : 'pl-10'
                    } pr-3 rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                  />
                  <span
                    className={`absolute inset-y-0 bottom-5 left-0 flex items-center pl-3 transition-opacity duration-300 ${
                      focusedInput ==='company_number'
                        ? 'opacity-0'
                        : 'opacity-100'
                    }`}
                  >
                    <img
                      src={phoneIcon}
                      className="h-5 w-5 text-[#575757]"
                      alt="Phone Icon"
                    />
                  </span>
                  {(!formData.company_number ||
                    focusedInput ==='company_number') && (
                    <label
                      htmlFor="companyNumber"
                      className={`absolute text-sm ${
                        focusedInput ==='company_number' ? 'left-1' : 'left-10'
                      }  top-3 text-[#575757] text-opacity-70 transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                    >
                      Enter company number
                    </label>
                  )}{' '}
                  <p
                    className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                      formErrors.company_number !== ''
                        ? 'opacity-100'
                        : 'opacity-0'
                    }`}
                  >
                    {formErrors.company_number || 'Company Number is required'}
                  </p>
                </div> */}

              {/* 5. Passwrord and confirm password fields */}
              <span className="font-roboto font-semibold text-sm text-gray-500">
                {' '}
                Create Password
              </span>

              <div className="grid grid-cols-2 gap-3.5">
                <div className="relative ">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    placeholder=" "
                    onFocus={() => setFocusedInput('password')}
                    onBlur={() => setFocusedInput('')}
                    onChange={handleInputChange}
                    value={formData.password}
                    className={`peer w-full border border-[#575757] bg-gray-100 border-opacity-30 p-3 ${
                      focusedInput === 'password' ? 'pl-4 pt-4 pb-2' : 'pl-10'
                    } pr-3 rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                  />
                  <span
                    className={`absolute inset-y-0 bottom-5 left-0 flex items-center pl-3 transition-opacity duration-300 peer-focus:opacity-0`}
                  >
                    <img
                      src={password}
                      className="h-5 w-5 text-[#575757]"
                      alt="password icon"
                    />
                  </span>
                  {(!formData.password || focusedInput === 'password') && (
                    <label
                      htmlFor="password"
                      className={`absolute  ${
                        focusedInput === 'password' ? 'left-1' : 'left-10'
                      }  top-3 text-[#575757] text-opacity-70 transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                    >
                      Password
                    </label>
                  )}
                  <p
                    className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                      formErrors.password !== '' ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {formErrors.password || 'Password is required'}
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirm_password"
                    placeholder=" "
                    onFocus={() => setFocusedInput('confirm_password')}
                    onBlur={() => setFocusedInput('')}
                    onChange={handleInputChange}
                    value={formData.confirm_password}
                    className={`peer w-full border border-[#575757] bg-gray-100 border-opacity-30 p-3 ${
                      focusedInput === 'confirm_password'
                        ? 'pl-4 pt-4 pb-2'
                        : 'pl-4'
                    } rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                  />
                  {(!formData.confirm_password ||
                    focusedInput === 'confirm_password') && (
                    <label
                      htmlFor="confirmPassword"
                      className={`absolute  ${
                        focusedInput === 'last_name' ? 'left-1' : 'left-3'
                      }  top-3 text-[#575757] text-opacity-70 transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                    >
                      Confirm Password
                    </label>
                  )}
                  <p
                    className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                      formErrors.confirm_password !== ''
                        ? 'opacity-100'
                        : 'opacity-0'
                    }`}
                  >
                    {formErrors.confirm_password ||
                      'Need to confirm your password'}
                  </p>
                </div>
              </div>
              {/* 6. Submit Button */}
              <button
                className="w-full bg-[rgba(118,54,217,1)] text-white mt-2 py-2 px-4 rounded-md hover:bg-purple-700 transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                onClick={handleSubmit}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span className="font-roboto font-semibold text-base">
                      Creating Account...
                    </span>
                  </>
                ) : (
                  <span className="font-roboto font-semibold text-base">
                    Create Account
                  </span>
                )}
              </button>

              <p className="text-center text-md mt-2 text-gray-600 font-roboto">
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                Already have an account?{' '}
                <span
                  className="cursor-pointer ml-1 text-[rgba(118,54,217,1)] font-semibold hover:underline"
                  onClick={() => navigate('/signin')}
                >
                  Sign In!
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SignUpPage);
